/**
 * <WeekSlotGrid> — la semaine, jour par jour, dont on coche les horaires (organism).
 *
 * Une rangée par jour (« Lun », « Mar »… dans l'ordre que l'écran passe), ses horaires dans
 * l'ordre de la journée, en cases `<WeekSlotChip>` (« 17h » : le jour est en tête de la rangée) qui
 * passent à la ligne quand la place manque : la même grille tient dans une feuille de téléphone et
 * dans un dialogue de 600 px. Sous la grille, la légende dit ce que chaque face veut dire, avec la
 * face elle-même en pastille, ses entrées côte à côte.
 *
 * Une case porte la pastille des tuteurs que le serveur a coloriés pour elle
 * (`tutorIds` → `tutors[].tone`) ; un cours que l'enfant garde est foncé et ne se coche pas ; le
 * cours qui change (`changingKeys`) a son heure barrée, pour qu'on voie celle qu'on quitte en
 * choisissant la suivante. La grille ne décide rien de tout cela : elle peint ce qui arrive.
 *
 * À côté de `<TimeSlotGrid>` (les disponibilités d'un cours ponctuel, des pilules toutes pareilles)
 * plutôt qu'en lui : ici une case porte un tuteur, une couleur et un état « déjà un cours », ce que
 * la pilule de sélection ne sait pas dire.
 *
 * Contrôlé : la sélection arrive (`selectedKeys`), une case repart par `onToggle`. Aucune copie :
 * la mention d'un cours gardé, la légende et le message d'une grille vide arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { WEEK_SLOT_CHIP_HEIGHT, WeekSlotChip } from "../../molecules/WeekSlotChip"
import {
    type UISlotChangeGridDay,
    type UISlotChangeGridSlot,
    type UISlotChangeTutor,
} from "../../../ports/primary/ui/types"
import { formatSlotHour, weeklySlotParts } from "../../../ports/primary/ui/formatters/weeklySlotFormatter"

/** La colonne des jours : « Lun » … « Dim » en `text-sm` gras y tiennent (27 px au plus à 13 px). */
const DAY_COLUMN_WIDTH = 40

/** Une ligne de légende : la face qu'elle explique, et ce qu'elle veut dire. */
export type WeekSlotLegendItem = {
    key: string
    label: string
    /** La couleur d'un tuteur ; absente, la case ordinaire. */
    tone?: SemanticColorKey | null
    /** La face d'un cours gardé. */
    course?: boolean
    /** La face du cours qui change, barrée. */
    changing?: boolean
}

export type WeekSlotGridProps = {
    days: UISlotChangeGridDay[]
    tutors: UISlotChangeTutor[]
    selectedKeys: string[]
    onToggle: (key: string) => void
    /** Les cases du cours qui change (des cours de l'enfant) : barrées, jamais cochables. */
    changingKeys?: string[]
    /** La mention sous l'heure d'un cours gardé — « cours ». */
    courseCaption?: string
    /** La mention sous l'heure barrée du cours qui change — « à changer ». */
    changingCaption?: string
    legend?: WeekSlotLegendItem[]
    /** Ce qu'on dit quand la grille n'a aucun horaire. */
    emptyMessage?: string
    testID?: string
}

/** Un tuteur colorié : sa couleur, pour sa pastille. */
const isTone = (tone: SemanticColorKey | null): tone is SemanticColorKey => tone != null

/** « 17h » — l'heure seule : le jour est déjà en tête de la rangée. */
function hourOf(slot: UISlotChangeGridSlot): string {
    const parts = weeklySlotParts(slot.label, slot.key)
    return parts != null ? formatSlotHour(parts, true) : slot.label
}

export function WeekSlotGrid(props: WeekSlotGridProps) {
    const {
        days,
        tutors,
        selectedKeys,
        onToggle,
        changingKeys = [],
        courseCaption,
        changingCaption,
        legend = [],
        emptyMessage,
        testID,
    } = props

    if (days.length === 0) {
        return emptyMessage != null && emptyMessage !== "" ? (
            <Text color="text-muted" testID={testID}>
                {emptyMessage}
            </Text>
        ) : null
    }

    const tutorsOf = (slot: UISlotChangeGridSlot) =>
        slot.tutorIds
            .map((id) => tutors.find((tutor) => String(tutor.id) === String(id)))
            .filter((tutor): tutor is UISlotChangeTutor => tutor != null && tutor.tone != null)

    return (
        <Stack gap="4" testID={testID}>
            <Stack gap="3">
                {days.map((day) => (
                    <Row key={day.id} gap="2" align="start">
                        <View style={styles.dayCell}>
                            <Text size="text-sm" weight="bold" color="text-muted">
                                {day.label}
                            </Text>
                        </View>
                        <View style={styles.slots}>
                            {day.slots.map((slot) => {
                                const slotTutors = tutorsOf(slot)
                                const changing = slot.course && changingKeys.includes(slot.key)
                                const caption = changing ? changingCaption : slot.course ? courseCaption : undefined
                                return (
                                    <WeekSlotChip
                                        key={slot.key}
                                        label={hourOf(slot)}
                                        selected={selectedKeys.includes(slot.key)}
                                        onPress={slot.course ? undefined : () => onToggle(slot.key)}
                                        tones={slotTutors.map((tutor) => tutor.tone).filter(isTone)}
                                        course={slot.course && !changing}
                                        changing={changing}
                                        caption={caption}
                                        accessibilityLabel={[
                                            slot.label,
                                            caption,
                                            ...slotTutors.map((tutor) => tutor.firstname),
                                        ]
                                            .filter(Boolean)
                                            .join(", ")}
                                    />
                                )
                            })}
                        </View>
                    </Row>
                ))}
            </Stack>
            {legend.length > 0 && (
                <View style={styles.legend}>
                    {legend.map((item) => (
                        <Row key={item.key} gap="1" align="center" style={styles.legendItem}>
                            <WeekSlotChip
                                label=""
                                swatch
                                tones={item.tone != null ? [item.tone] : []}
                                course={item.course}
                                changing={item.changing}
                            />
                            <Text size="text-sm" color="text-muted" style={styles.legendText}>
                                {item.label}
                            </Text>
                        </Row>
                    ))}
                </View>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le jour se centre sur la première ligne de cases. */
    dayCell: {
        width: DAY_COLUMN_WIDTH,
        minHeight: WEEK_SLOT_CHIP_HEIGHT,
        justifyContent: "center" as const,
    },
    slots: {
        flex: 1,
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        gap: theme.spacing["2"],
    },
    legend: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        columnGap: theme.spacing["3"],
        rowGap: theme.spacing["2"],
    },
    legendItem: { flexShrink: 1 },
    legendText: { flexShrink: 1 },
}))
